/**
 * isolateHandRegion — the E2b Load-side bridge (ADR-010 §2 ① "Isolate", Phase B).
 * ONE responsibility: from a loaded three.js SkinnedMesh + a resolved hand bone,
 * cut out the hand-only triangle mesh and the wrist cut plane that E2's
 * {@link voxelizeMesh} needs — the exact inputs the kernel landmarker (E1) then
 * reads AS TOPOLOGY. Format/engine knowledge ends here; the kernel never sees a
 * three object.
 *
 * The hand vertex set is the hand bone's skin-weight influence region (§2 ①:
 * "the hand vertex set is the Hand bone's influence region"): a vertex belongs
 * when its DOMINANT (largest-weight) bone is the hand bone or one of its bone
 * descendants (future-proofs rigs that DO carry finger bones; a raw Tripo hand
 * has none). Dominant, not any-weight, so forearm-cuff blends do not leak in.
 *
 * Everything is computed in the skeleton's BIND-WORLD space, pose-independently:
 * vertices as `bindMatrix · geometryPosition` (the synthesize-tips idiom, ADR-003
 * §2b) and bone origins from the skeleton's inverse-bind matrices (`boneInverses`,
 * IBM-derived — identical to the kernel's `worldBind`, so a clip left applied to
 * the scene cannot skew the cut). The wrist plane sits at the proximal start of
 * the hand slab with its normal pointing forearm→hand — the orientation E2's
 * `cutPlane` seam expects (seed = hand-side voxels within one voxelSize).
 *
 * Fail-loud (ADR-008 §7, LAWS "fail loudly over silent fallback"): a mesh without
 * skin attributes, a hand bone absent from the skeleton, or a hand region that
 * yields zero triangles all throw {@link HandIsolationError} — never a silent
 * empty hand. (A wrist plane that then seeds nothing is E2's fail-loud, and a
 * cut that lands off the volume is E1's honest `invalid-grid` result — §11.)
 *
 * Depends on: three, @harryhausen/kernel (CutPlane type). Depended on by: the E2b
 * capture script + test, and (later) the app Hand Studio isolate stage.
 */
import { Bone, Matrix4, Object3D, SkinnedMesh, Vector3 } from 'three';
import type { CutPlane, Positions } from '@harryhausen/kernel';
import { AdapterError } from '../errors.js';
import { isViewportDebris } from '../viewport-debris.js';

/** A hand region that cannot be isolated — a producer/caller bug, fail-loud. */
export class HandIsolationError extends AdapterError {
  constructor(
    readonly boneName: string,
    readonly reason: string,
  ) {
    super(`Cannot isolate hand region for bone "${boneName}": ${reason} (ADR-010 §2 ①).`);
  }
}

/**
 * How far hand flesh may reach from the wrist, in forearm lengths. MEASURED 2026-09-29 over the 34
 * fixture hands: 0.72–1.65 (Koda's and Gilda's big furred paws at the top — a first cap of 1.5 cut
 * Koda's and failed her golden); Daria's wing-weighted hands 4.1–4.75. 2.5 sits between, with margin.
 */
export const HAND_REACH_FOREARMS = 2.5;
/**
 * A hand WITH digit bones: its flesh may reach this many times the digits' own bone reach from the
 * wrist. MEASURED 2026-09-29 over 20 fingered hands (ActorCore Tripos, Shirley): flesh ÷ bone reach
 * 1.12–1.33; Daria's whole hand 5.65–5.74 — Tripo hung her WING on the middle-finger chain (2,825
 * vertices to 48 cm). Her TALONS run past their bones (per chain: ring 1.66, pinky 1.89), so a first
 * cap of 1.6 cut the ring and pinky tips and refused their pins (Allen, 2026-09-30: "it wouldn't let
 * me pin the fourth digit"). 2.0 keeps the talons and every real hand. The bones say how big the hand
 * is even when the weights lie.
 */
export const HAND_REACH_DIGITS = 2.0;

export interface IsolateHandOptions {
  /**
   * Include vertices dominated by a bone DESCENDANT of the hand bone, not just
   * the hand bone itself (default true). A raw generated hand has no finger
   * bones so this is a no-op there, but a rig that already has fingers keeps
   * its whole hand as one region.
   */
  readonly includeDescendants?: boolean;
  /**
   * A triangle joins the hand mesh when all 3 (`all-in`, default) or at least 2
   * (`majority`) of its vertices are in the region. `all-in` gives the cleanest
   * wrist boundary; `majority` closes it tighter at the cost of a cuff fringe.
   */
  readonly trianglePolicy?: 'all-in' | 'majority';
  /**
   * SOURCE vertex indices a person's WRIST STATION re-owned for this hand
   * (ADR-025 D2): flesh Tripo weighted to the severed forearm chain that sits
   * distal of the confirmed wrist, within the hand's reach — the palm root, and
   * a misweighted thumb lobe like Cairn's. Isolated as hand flesh, with their
   * boundary skirt kept so the lobe voxelizes without a hole. Absent or empty:
   * the region is the dominant-weight region exactly as before (D4 — every
   * golden baseline is cut with this empty). Computed by the kernel's
   * `reownedVertices`; this door only applies it. It REPLACES the ADR-024-era
   * distal-lobe heuristic that guessed at the same flesh from the joint that
   * was wrong (ADR-025 §5).
   */
  readonly owned?: ReadonlySet<number>;
  /**
   * The hand's DIGIT BONE reach from the wrist (bind-world), when the caller knows it better than the
   * skeleton does — the run, which stripped a replaced hand's native digits before isolating it, passes
   * the reach measured BEFORE the strip. Absent: read from the hand bone's skeleton descendants.
   */
  readonly digitReach?: number;
}

export interface IsolatedHand {
  /**
   * Vertices RE-OWNED from the forearm chain by a confirmed wrist station
   * (ADR-025; 0 when no station was confirmed, and on a cleanly weighted rig):
   * flesh whose dominant bone is the severed forearm side but which sits
   * distal of the wrist, within the hand's own reach. Measured into existence
   * by Cairn (2026-08-14): its left thumb — 31 vertices extending 26mm past
   * the wrist — is dominated by `L_ForearmTwist02`, so the thumb was invisible
   * to the volume and unpinnable on the surface. THE WRIST IS THE BOUNDARY
   * AUTHORITY, NOT THE WEIGHTS — on a fingerless generated rig the weights are
   * precisely the thing being repaired. Reported, never silent.
   */
  readonly reclaimedVertexCount: number;
  /**
   * SOURCE indices of vertices that entered ONLY as skirt riders — the far
   * corners of a reclaimed lobe's boundary triangles, genuine cuff flesh kept
   * so the lobe voxelizes without a hole (`corePass`). They shape the VOLUME
   * and must NOT be re-weighted: measured on the `_new_v3` batch, re-solving
   * cuff-seam vertices among grafted finger bones is exactly "the fingers
   * are swollen" (Cairn L 33, Nathan L 24 foreign vertices in the bind).
   * Empty on a clean hand. The bind subtracts these from its vertex set.
   */
  readonly volumeOnlyVertexIndices: ReadonlySet<number>;
  /** Hand-region vertices, flat `[x,y,z,...]` in skeleton bind-world space. */
  readonly positions: Float32Array;
  /**
   * For each isolated vertex, the index it had in the SOURCE geometry. The
   * isolation compacts and reindexes, which is right for handing a standalone hand
   * mesh to the voxelizer — but Bind (§2 ④) has to write skin weights back into the
   * original attributes, and without this the way home is lost. One entry per
   * {@link vertexCount}; parallel to `positions`.
   */
  readonly vertexIndices: Uint32Array;
  /** Triangles as indices into {@link positions} (3 per triangle). */
  readonly indices: Uint32Array;
  /** The wrist cut plane for {@link voxelizeMesh} — normal points toward the hand. */
  readonly cutPlane: CutPlane;
  readonly vertexCount: number;
  readonly triangleCount: number;
  /** The hand bone whose influence region this is. */
  readonly boneName: string;
  /** The forearm (parent) bone the wrist normal was derived from, or null. */
  readonly forearmName: string | null;
}

/** The bind-world origin of a skeleton bone from its inverse-bind matrix (pose-independent). */
function boneBindPosition(mesh: SkinnedMesh, boneIndex: number): Vector3 {
  const ibm = mesh.skeleton.boneInverses[boneIndex];
  if (ibm === undefined) {
    throw new HandIsolationError(
      mesh.skeleton.bones[boneIndex]?.name ?? `#${boneIndex}`,
      `skeleton has no inverse-bind matrix at index ${boneIndex}`,
    );
  }
  // IBM maps bind-world → bone-local; its inverse maps the bone origin back to
  // bind-world. Read the translation column of the inverse.
  return new Vector3().setFromMatrixPosition(new Matrix4().copy(ibm).invert());
}

/** Skeleton indices of `bone` plus (optionally) every Bone in its subtree. */
function handBoneIndices(mesh: SkinnedMesh, hand: Bone, includeDescendants: boolean): Set<number> {
  const bones = mesh.skeleton.bones;
  const wanted = new Set<Object3D>([hand]);
  if (includeDescendants) {
    hand.traverse((o) => {
      if ((o as Bone).isBone) wanted.add(o);
    });
  }
  const indices = new Set<number>();
  bones.forEach((b, i) => {
    if (wanted.has(b)) indices.add(i);
  });
  if (indices.size === 0) {
    throw new HandIsolationError(hand.name, 'bone is not part of this mesh’s skeleton');
  }
  return indices;
}

/**
 * Cut the hand-only mesh + wrist plane from a SkinnedMesh. `handBone` and
 * `forearmBone` are resolved by the caller (in the app: BBSA `LeftHand`/
 * `RightHand` → rig bone through the ontology mapping; the capture script/test
 * pass them by name). See the module header for the full contract.
 */
export function isolateHandRegion(
  mesh: SkinnedMesh,
  handBone: Bone,
  forearmBone: Bone | null,
  options: IsolateHandOptions = {},
): IsolatedHand {
  const includeDescendants = options.includeDescendants ?? true;
  const trianglePolicy = options.trianglePolicy ?? 'all-in';

  const position = mesh.geometry.getAttribute('position');
  const skinIndex = mesh.geometry.getAttribute('skinIndex');
  const skinWeight = mesh.geometry.getAttribute('skinWeight');
  if (!position) throw new HandIsolationError(handBone.name, 'mesh geometry has no position attribute');
  if (!skinIndex || !skinWeight) {
    throw new HandIsolationError(handBone.name, 'mesh is not skinned (no skinIndex/skinWeight) — nothing to isolate by');
  }

  const region = handBoneIndices(mesh, handBone, includeDescendants);
  const bind = new Matrix4().copy(mesh.bindMatrix);
  const vCount = position.count;

  // THE HAND'S REACH (2026-09-29): hand flesh farther than HAND_REACH_FOREARMS forearm-lengths
  // from the wrist joint is not hand. Daria (a winged Tripo dragon) has her WING MEMBRANE weighted
  // to the hand bones: 2,973 vertices reaching 48 cm on an 11.6 cm forearm (4.1×), so the pinner
  // measured a half-metre "hand" — voxels, discs and fingertips at the wing tips. The fixture hands
  // reach 0.72–1.65× (see HAND_REACH_FOREARMS), so every one of them is unchanged.
  // The wing still moves with the hand; it is just not measured as fingers. No forearm: no cap.
  const skeletonIndexOf = (b: Bone): number => mesh.skeleton.bones.indexOf(b);
  const bindOf = (b: Bone): Vector3 | null => {
    const i = skeletonIndexOf(b);
    return i < 0 ? null : new Vector3().setFromMatrixPosition(new Matrix4().copy(mesh.skeleton.boneInverses[i]!).invert());
  };
  const wristAt = bindOf(handBone);
  const elbowAt = forearmBone === null ? null : bindOf(forearmBone);
  const forearmCap = wristAt !== null && elbowAt !== null ? HAND_REACH_FOREARMS * wristAt.distanceTo(elbowAt) : Infinity;
  // …and a hand with digit bones is as big as its digits say (HAND_REACH_DIGITS), whatever its weights claim.
  let digitReach = options.digitReach ?? 0;
  if (options.digitReach === undefined && wristAt !== null) {
    handBone.traverse((o) => {
      if (o === handBone) return;
      const at = bindOf(o as Bone);
      if (at !== null) digitReach = Math.max(digitReach, at.distanceTo(wristAt));
    });
  }
  const maxReach = digitReach > 0 ? Math.min(forearmCap, HAND_REACH_DIGITS * digitReach) : forearmCap;
  const reachProbe = new Vector3();
  const beyondReach = (i: number): boolean => {
    if (!Number.isFinite(maxReach) || wristAt === null) return false;
    reachProbe.set(position.getX(i), position.getY(i), position.getZ(i)).applyMatrix4(bind);
    return reachProbe.distanceTo(wristAt) > maxReach;
  };

  /** Dominant (largest-weight) skeleton bone of vertex `i`, or -1 when unweighted. */
  const dominantBone = (i: number): number => {
    let bestBone = -1;
    let bestW = -1;
    for (let k = 0; k < 4; k += 1) {
      const w = skinWeight.getComponent(i, k);
      if (w > bestW) {
        bestW = w;
        bestBone = skinIndex.getComponent(i, k);
      }
    }
    return bestW > 0 ? bestBone : -1;
  };

  /**
   * One full isolation pass — TODAY'S algorithm, verbatim, over the region
   * plus `extra` reclaimed vertex indices (empty on the first pass, so the
   * first pass is bit-identical to what every golden baseline was cut with).
   */
  const corePass = (extra: ReadonlySet<number>): Omit<IsolatedHand, 'reclaimedVertexCount' | 'volumeOnlyVertexIndices'> & { riders: Set<number> } => {
    const riders = new Set<number>();
    // --- membership by DOMINANT bone; collect region vertices in bind-world space ---
    const inRegion = new Uint8Array(vCount);
    const worldPos: Vector3[] = [];
    const oldToNew = new Int32Array(vCount).fill(-1);
    const newToOld: number[] = [];
    const outPos: number[] = [];
    const v = new Vector3();
    for (let i = 0; i < vCount; i += 1) {
      const bestBone = dominantBone(i);
      if ((bestBone < 0 || !region.has(bestBone)) && !extra.has(i)) continue; // unweighted or not-hand
      if (!extra.has(i) && beyondReach(i)) continue; // beyond a hand's reach (a wing weighted to the hand)
      inRegion[i] = 1;
      v.set(position.getX(i), position.getY(i), position.getZ(i)).applyMatrix4(bind);
      oldToNew[i] = worldPos.length;
      newToOld.push(i);
      worldPos.push(v.clone());
      outPos.push(v.x, v.y, v.z);
    }
    if (worldPos.length === 0) {
      throw new HandIsolationError(handBone.name, 'no vertex is dominated by the hand bone (empty influence region)');
    }

    // --- triangles: keep per policy, reindex to the compacted vertex set ---
    const geoIndex = mesh.geometry.getIndex();
    const triCount = geoIndex ? geoIndex.count / 3 : vCount / 3;
    const minInside = trianglePolicy === 'majority' ? 2 : 3;
    const outIdx: number[] = [];
    for (let t = 0; t < triCount; t += 1) {
      const a = geoIndex ? geoIndex.getX(t * 3) : t * 3;
      const b = geoIndex ? geoIndex.getX(t * 3 + 1) : t * 3 + 1;
      const c = geoIndex ? geoIndex.getX(t * 3 + 2) : t * 3 + 2;
      const inside = (inRegion[a] ?? 0) + (inRegion[b] ?? 0) + (inRegion[c] ?? 0);
      // A RECLAIMED LOBE KEEPS ITS FULL SKIRT: the boundary triangles that
      // stitch it to the cuff and palm fail 'all-in' (their far corners are
      // legitimately not hand flesh), and on a 125-vertex hand the missing
      // skirt is a voxel GAP at the thumb root — Eric_new_v3's knuckle click
      // measured 2.1–2.6 voxels off the volume through exactly that hole
      // (field, 2026-08-14), refusing a graft the person had aimed correctly.
      // Clean hands: `extra` is empty and this clause never fires.
      const touchesReclaim = extra.size > 0 && (extra.has(a) || extra.has(b) || extra.has(c));
      if (inside < minInside && !touchesReclaim) continue;
      // Under 'majority' — or as a reclaimed lobe's skirt — an out-of-region
      // corner has no compacted index: pull it in so the triangle is
      // well-formed. It becomes a boundary vertex that shapes the volume; a
      // skirt RIDER is remembered so the bind can leave its weights alone.
      for (const oldIdx of [a, b, c]) {
        if (oldToNew[oldIdx] === -1) {
          oldToNew[oldIdx] = worldPos.length;
          newToOld.push(oldIdx);
          v.set(position.getX(oldIdx), position.getY(oldIdx), position.getZ(oldIdx)).applyMatrix4(bind);
          worldPos.push(v.clone());
          outPos.push(v.x, v.y, v.z);
          if (touchesReclaim && !extra.has(oldIdx)) riders.add(oldIdx);
        }
      }
      outIdx.push(oldToNew[a]!, oldToNew[b]!, oldToNew[c]!);
    }
    if (outIdx.length === 0) {
      throw new HandIsolationError(
        handBone.name,
        `hand region has ${worldPos.length} vertices but no triangle meets the '${trianglePolicy}' policy`,
      );
    }

    // --- wrist cut plane: normal forearm→hand, placed at the proximal hand slab ---
    const handOrigin = boneBindPosition(mesh, mesh.skeleton.bones.indexOf(handBone));
    let normal: Vector3;
    let forearmName: string | null = null;
    if (forearmBone) {
      forearmName = forearmBone.name;
      const forearmOrigin = boneBindPosition(mesh, mesh.skeleton.bones.indexOf(forearmBone));
      normal = new Vector3().subVectors(handOrigin, forearmOrigin);
    } else {
      // No forearm: point from the wrist toward the hand-region centroid.
      const centroid = new Vector3();
      for (const p of worldPos) centroid.add(p);
      centroid.multiplyScalar(1 / worldPos.length);
      normal = new Vector3().subVectors(centroid, handOrigin);
    }
    if (!(normal.lengthSq() > 0)) {
      throw new HandIsolationError(handBone.name, 'wrist normal is degenerate (hand and forearm origins coincide)');
    }
    normal.normalize();

    // Place the plane at the most-proximal hand vertex along the normal (but never
    // proximal of the wrist joint) so E2's one-voxelSize seed band lands on the
    // real wrist shell, not in the empty forearm gap left by the triangle cut.
    // When the band STILL comes up empty (a stray proximal mesh island owning the
    // minimum — barbarian R's bracer ring, 2026-07-28), the repair lives in E2:
    // voxelizeMesh re-bands onto the largest voxel component and records the
    // liberty on the grid. Two isolation-side cures were tried and REVERTED the
    // same day — both shifted the kobold's golden baselines (an allowed-proximal
    // clamp, and anchoring to the largest mesh component): the plane here must
    // stay bit-identical for every already-working hand.
    let projMin = Infinity;
    for (const p of worldPos) {
      const d = p.clone().sub(handOrigin).dot(normal);
      if (d < projMin) projMin = d;
    }
    const point = handOrigin.clone().addScaledVector(normal, Math.max(0, projMin));

    return {
      positions: new Float32Array(outPos),
      vertexIndices: new Uint32Array(newToOld),
      indices: new Uint32Array(outIdx),
      cutPlane: { point: [point.x, point.y, point.z], normal: [normal.x, normal.y, normal.z] },
      vertexCount: worldPos.length,
      triangleCount: outIdx.length / 3,
      boneName: handBone.name,
      forearmName,
      riders,
    };
  };

  const finish = (pass: ReturnType<typeof corePass>, reclaimedCount: number, plane: CutPlane): IsolatedHand => {
    const { riders, ...rest } = pass;
    return { ...rest, cutPlane: plane, reclaimedVertexCount: reclaimedCount, volumeOnlyVertexIndices: riders };
  };

  const first = corePass(new Set<number>());

  // ── THE WRIST STATION'S RE-OWNERSHIP (ADR-025 D2) ────────────────────────
  // BIT-IDENTITY FOR EVERY HAND WITHOUT A CONFIRMED STATION, by construction:
  // the plane above is computed from the dominant-weight region exactly as it
  // always was, and an empty `owned` returns the first pass untouched (D4).
  // With a non-empty set the second pass re-runs the same algorithm over
  // region ∪ owned and TAKES ITS OWN PLANE: the plane hangs from the (moved)
  // joint and sits at the most-proximal vertex of the region — with the palm
  // root re-owned, that IS the station; the first pass's plane, computed from
  // hand-weight alone, was clamped to where Tripo's weights began (measured
  // 2026-08-15 on Kara: 12%L distal of the station), and the voxelizer would
  // have cut the very flesh the station re-owned.
  const owned = options.owned ?? new Set<number>();
  if (owned.size === 0) return finish(first, 0, first.cutPlane);
  const second = corePass(owned);
  return finish(second, owned.size, second.cutPlane);
}

/**
 * Per-vertex bind-world ownership — the evidence `reownedVertices` (kernel,
 * ADR-025) reads: each vertex's bind-world position and its DOMINANT skeleton
 * bone index (-1 when unweighted). Adapter-side because it reads three's
 * attributes; the rule that consumes it is the kernel's.
 */
export function bindWorldOwnership(mesh: SkinnedMesh): { index: number; position: { x: number; y: number; z: number }; dominantBone: number }[] {
  const position = mesh.geometry.getAttribute('position');
  const skinIndex = mesh.geometry.getAttribute('skinIndex');
  const skinWeight = mesh.geometry.getAttribute('skinWeight');
  if (!position || !skinIndex || !skinWeight) {
    throw new HandIsolationError(mesh.name || '(mesh)', 'mesh is not skinned (no position/skinIndex/skinWeight)');
  }
  const bind = new Matrix4().copy(mesh.bindMatrix);
  const v = new Vector3();
  const out: { index: number; position: { x: number; y: number; z: number }; dominantBone: number }[] = [];
  for (let i = 0; i < position.count; i += 1) {
    let bestBone = -1;
    let bestW = -1;
    for (let k = 0; k < 4; k += 1) {
      const w = skinWeight.getComponent(i, k);
      if (w > bestW) {
        bestW = w;
        bestBone = skinIndex.getComponent(i, k);
      }
    }
    v.set(position.getX(i), position.getY(i), position.getZ(i)).applyMatrix4(bind);
    out.push({ index: i, position: { x: v.x, y: v.y, z: v.z }, dominantBone: bestW > 0 ? bestBone : -1 });
  }
  return out;
}

/**
 * The SEVERED forearm chain: skeleton indices of the forearm bone and its
 * descendants (twist helpers included) MINUS the hand subtree — the bones whose
 * flesh a wrist station may re-own (ADR-025 D2). The hand subtree is not the
 * severed side; it is the hand.
 */
export function severedForearmChain(mesh: SkinnedMesh, forearmBone: Bone, handBone: Bone): Set<number> {
  const wanted = new Set<Object3D>();
  forearmBone.traverse((o) => {
    if ((o as Bone).isBone) wanted.add(o);
  });
  handBone.traverse((o) => {
    if ((o as Bone).isBone) wanted.delete(o);
  });
  const chain = new Set<number>();
  mesh.skeleton.bones.forEach((b, i) => {
    if (wanted.has(b)) chain.add(i);
  });
  return chain;
}

/** Narrow a scene object to a SkinnedMesh (traversal predicate). */
export function isSkinnedMesh(o: Object3D): o is SkinnedMesh {
  return (o as SkinnedMesh).isSkinnedMesh === true;
}

/**
 * Find THE single SkinnedMesh in a scene. Fails loud on 0 (nothing to isolate)
 * and on >1 (the hand geometry could span meshes — silently taking the first
 * would isolate an incomplete hand). A multi-mesh picker is a follow-up; every
 * current specimen is one skinned mesh.
 */
export function soleSkinnedMesh(scene: Object3D): SkinnedMesh {
  const found: SkinnedMesh[] = [];
  scene.traverse((o) => {
    // The weight overlay's ghost is itself a SkinnedMesh child of the subject
    // and is not the subject — counting it made every hand measurement refuse
    // as "2 skinned meshes" the moment W had been pressed, and hiding it did
    // not recover (Codex P1, #98 round six). Identity is the userData stamp
    // the overlay sets at creation, never the node NAME: an imported model's
    // mesh wearing any of our names is somebody's geometry and must count
    // (Codex P2s, rounds seven and nine).
    if (isSkinnedMesh(o) && !isViewportDebris(o)) found.push(o);
  });
  if (found.length === 0) throw new AdapterError('scene contains no SkinnedMesh to isolate a hand from.');
  if (found.length > 1) {
    throw new AdapterError(
      `scene has ${found.length} skinned meshes (${found.map((m) => m.name || '<unnamed>').join(', ')}); ` +
        `hand geometry may span them — resolve the hand mesh explicitly (multi-mesh isolation is a follow-up).`,
    );
  }
  return found[0]!;
}

/**
 * EVERY skinned mesh in a scene, ghost-excluded, fail-loud on none. The hinge
 * pass (ADR-028) needs this where the hand pipeline needs `soleSkinnedMesh`:
 * a clothed subject carries body + garment meshes, BOTH with arm-chain
 * weights, and seating/reweighting only "the" mesh would throw on load for a
 * subject that loaded fine before correctives existed (Codex P2, #103) —
 * or silently leave the garment's crease weights uncorrected.
 */
export function allSkinnedMeshes(scene: Object3D): SkinnedMesh[] {
  const found: SkinnedMesh[] = [];
  scene.traverse((o) => {
    if (isSkinnedMesh(o) && !isViewportDebris(o)) found.push(o);
  });
  if (found.length === 0) throw new AdapterError('scene contains no SkinnedMesh.');
  return found;
}

/** Look up a skeleton bone by exact name from any SkinnedMesh, or null. */
export function findBoneByName(mesh: SkinnedMesh, name: string): Bone | null {
  return mesh.skeleton.bones.find((b) => b.name === name) ?? null;
}

// Re-export the E2 seam type so callers of this Load-side module get it here.
export type { CutPlane, Positions };
